import localforage from 'localforage';
import { formatDateForStorage } from '../../../utils/dateUtils';

const STORAGE_KEY = 'ticketComments_v1';
const EMPTY_DATA = { Items: [] };

// Normalizes seeded comment timestamps so each ticket's defaults appear in time order.
const normalizeDefaultCommentDates = (data, now = new Date()) => {
  if (!data || !Array.isArray(data.Items)) {
    return EMPTY_DATA;
  }

  const itemsByTicket = new Map();

  data.Items.forEach((item, index) => {
    const ticketId = item?.ticketId ?? item?.ticketID;
    if (ticketId === undefined || ticketId === null) {
      return;
    }

    // Group comments by ticket ID. We will sort and assign dates within each ticket group to ensure
    // a logical order, even if the source data is not ordered or has non-sequential IDs.
    const ticketKey = String(ticketId);
    if (!itemsByTicket.has(ticketKey)) {
      itemsByTicket.set(ticketKey, []);
    }

    itemsByTicket.get(ticketKey).push({ item, index });
  });

  // Sort comments within each ticket group and assign createDateTime/lastActivityDate values.
  itemsByTicket.forEach((ticketItems) => {
    ticketItems.sort((left, right) => {
      const leftId = Number(left.item.id);
      const rightId = Number(right.item.id);
      const leftValid = Number.isFinite(leftId);
      const rightValid = Number.isFinite(rightId);

      if (leftValid && rightValid && leftId !== rightId) {
        return leftId - rightId;
      }

      if (leftValid !== rightValid) {
        return leftValid ? -1 : 1;
      }

      return left.index - right.index;
    });

    // Each ticket may have one or more comments. We've sorted the comments into ID order at this point, which 
    // means that the earliest comment will be assigned the earliest timestamp. We calculate a date/time
    // {number of comments - 1} hours ago as the starting date so that the final comment in the sequence
    // appears to have been created just now and each earlier comment appears one hour before the next.
    // Reset the first comment's time to be {number of comments - 1} hours ago.
    const firstCommentTime = new Date(now.getTime() - ((ticketItems.length - 1) * 60 * 60 * 1000));

    ticketItems.forEach(({ item }, offset) => {
      const commentTime = new Date(firstCommentTime.getTime() + (offset * 60 * 60 * 1000));
      const formattedDate = formatDateForStorage(commentTime);

      item.createDateTime = formattedDate;
      item.lastActivityDate = formattedDate;
    });
  });

  return data;
};

// The ticket comment data is held as a static JSON file. If the data is not present in the local
// store (on the first run, or if the data has been reset), it is reloaded from the file. This 
// means that to change the data, you may need to both change the static data file AND run the
// "Clear Local Data" page under Admin => Advanced settings.
// Used by read() and resetToDefaults() to repopulate IndexedDB from fixture data.
const loadDefaults = async () => {
  try {
    const defaults = require('./_local/_ticketCommentData.json');
    const clonedDefaults = JSON.parse(JSON.stringify(defaults || EMPTY_DATA));
    return normalizeDefaultCommentDates(clonedDefaults);
  } catch (e) {
    return EMPTY_DATA;
  }
};

// Reads comments from localforage. On first run, it seeds storage from defaults.
// This helper is used by all exported operations to ensure consistent local state.
const read = async () => {
  try {
    const raw = await localforage.getItem(STORAGE_KEY);
    if (!raw) {
      const defaults = await loadDefaults();
      await localforage.setItem(STORAGE_KEY, defaults);
      return defaults;
    }
    return raw;
  } catch (e) {
    return { Items: [] };
  }
};

// Persists the full comment collection back into localforage.
// Called after mutations such as adding comments or resetting defaults.
const write = async (data) => {
  await localforage.setItem(STORAGE_KEY, data);
};

const getCommentIdentity = (comment) => {
  if (!comment) return null;

  const rawId = comment.id ?? comment.ID;
  if (rawId === undefined || rawId === null || rawId === '') {
    return null;
  }

  const numericId = Number(rawId);
  if (Number.isFinite(numericId)) {
    return `id:${numericId}`;
  }

  return `id:${String(rawId)}`;
};

const getDefaultCommentIdentitySet = () => {
  const defaults = require('./_local/_ticketCommentData.json') || EMPTY_DATA;
  const defaultItems = Array.isArray(defaults?.Items) ? defaults.Items : [];
  return new Set(defaultItems.map(getCommentIdentity).filter(Boolean));
};

// Returns notes present in local storage that are not part of the seeded
// _ticketCommentData fixture. These are the notes we include in backups.
export const getUserAddedComments = async () => {
  const data = await read();
  const items = Array.isArray(data?.Items) ? data.Items : [];
  const defaultIdentitySet = getDefaultCommentIdentitySet();

  return items.filter((comment) => {
    const identity = getCommentIdentity(comment);
    return identity && !defaultIdentitySet.has(identity);
  });
};

// Restores backed-up notes into local storage.
// 1) Every imported note gets a new numeric id (to avoid local id collisions).
// 2) ticketID/ticketId is remapped via ticketIdMap so notes still attach to
//    the matching imported ticket after that ticket receives a new id.
// 3) createdByContactID is optionally remapped via contactIdMap.
export const restoreUserAddedComments = async (comments, ticketIdMap = null, contactIdMap = null) => {
  if (!Array.isArray(comments)) {
    throw new Error('restoreUserAddedComments expects an array of comments.');
  }

  const data = await read();
  const existingItems = Array.isArray(data?.Items) ? data.Items : [];

  let nextGeneratedId = existingItems.reduce((max, comment) => {
    const rawId = comment?.id ?? comment?.ID;
    const numericId = Number(rawId);
    if (!Number.isFinite(numericId)) {
      return max;
    }

    return numericId > max ? numericId : max;
  }, 0);

  const itemsToAdd = [];
  let skippedCount = 0;

  const remapValue = (value, map) => {
    if (!map || typeof map.get !== 'function') {
      return value;
    }
    if (value === undefined || value === null || value === '') {
      return value;
    }

    const mapped = map.get(String(value));
    return mapped !== undefined ? mapped : value;
  };

  const getCommentSignature = (comment) =>
    JSON.stringify([
      String(comment?.ticketID ?? comment?.ticketId ?? ''),
      String(comment?.createdByContactID ?? ''),
      String(comment?.createDateTime ?? ''),
      String(comment?.title ?? ''),
      String(comment?.description ?? ''),
    ]);

  const existingSignatureSet = new Set(existingItems.map(getCommentSignature));

  for (const comment of comments) {
    if (!comment || typeof comment !== 'object') {
      skippedCount += 1;
      continue;
    }

    const importedComment = { ...comment };

    importedComment.ticketID = remapValue(importedComment.ticketID, ticketIdMap);
    importedComment.ticketId = remapValue(importedComment.ticketId, ticketIdMap);
    importedComment.createdByContactID = remapValue(importedComment.createdByContactID, contactIdMap);

    nextGeneratedId += 1;
    importedComment.id = nextGeneratedId;
    if (Object.prototype.hasOwnProperty.call(importedComment, 'ID')) {
      importedComment.ID = nextGeneratedId;
    }

    itemsToAdd.push(importedComment);
  }

  data.Items = [...existingItems, ...itemsToAdd];
  await write(data);

  return {
    totalInFile: comments.length,
    addedCount: itemsToAdd.length,
    skippedCount,
  };
};

// This is called by the "Clear Local Data" page under Admin => Advanced settings
// Reloads fixture comments and overwrites any in-browser edits in local storage.
export const resetToDefaults = async () => {
  const defaults = await loadDefaults();
  await write(defaults);
  return defaults;
};

// Applies the date-adjustment logic to the comments currently in the store without
// clearing any records. Used by the "Reset Local Data Dates" page under Admin => Advanced settings.
export const refreshDates = async () => {
  const data = await read();
  const currentItems = Array.isArray(data?.Items) ? data.Items : [];

  const defaults = require('./_local/_ticketCommentData.json') || EMPTY_DATA;
  const defaultItems = Array.isArray(defaults?.Items) ? defaults.Items : [];
  const defaultIdSet = new Set(defaultItems.map((item) => String(item?.id)).filter(Boolean));

  const seededSubset = {
    Items: currentItems
      .filter((item) => defaultIdSet.has(String(item?.id)))
      .map((item) => JSON.parse(JSON.stringify(item)))
  };

  const normalizedSeededSubset = normalizeDefaultCommentDates(seededSubset);
  const normalizedById = new Map(
    (normalizedSeededSubset?.Items || []).map((item) => [String(item?.id), item])
  );

  const refreshed = {
    ...data,
    Items: currentItems.map((item) => {
      const id = String(item?.id);
      return normalizedById.has(id) ? normalizedById.get(id) : item;
    })
  };

  await write(refreshed);
  return refreshed;
};

// This is used to get all comments for a specific ticket in the View Ticket page.
// Returns all comments if no ticketID is provided; otherwise filters by ticketID.
export const getCommentsForTicket = async (ticketID) => {
  const data = await read();
  const sortByCreatedDateDescending = (items) => {
    return [...items].sort((left, right) => {
      const leftTime = new Date(left?.createDateTime).getTime();
      const rightTime = new Date(right?.createDateTime).getTime();
      const leftValid = Number.isFinite(leftTime);
      const rightValid = Number.isFinite(rightTime);

      if (leftValid && rightValid) {
        return rightTime - leftTime;
      }

      if (leftValid !== rightValid) {
        return leftValid ? -1 : 1;
      }

      return 0;
    });
  };

  if (!ticketID) {
    return { Items: sortByCreatedDateDescending(data.Items || []) };
  }

  const matches = (data.Items || []).filter((c) => {
    if (!c) return false;

    const commentTicketId = c.ticketID ?? c.ticketId;
    return commentTicketId === Number(ticketID) || String(commentTicketId) === String(ticketID);
  });
  return { Items: sortByCreatedDateDescending(matches) };
};

// Returns a single note by id from local storage in an Items collection shape.
export const getNoteById = async (id) => {
  const data = await read();
  const items = data.Items || [];
  const idNum = Number(id);
  const idStr = String(id);

  const match = items.find((c) => {
    if (!c) return false;
    const rawId = c.id ?? c.ID;
    return (Number.isFinite(idNum) && Number(rawId) === idNum) || String(rawId) === idStr;
  });

  return match ? { Items: [match] } : { Items: [] };
};

// This adds a comment to the store. It is used by the View Ticket page.
// Appends a single comment object and persists the updated collection.
export const addComment = async (comment) => {
  const data = await read();
  data.Items.push(comment);
  await write(data);
  return comment;
};

// If we're not running locally, we get the new ticket note ID from Autotask, but
// in local mode, we need to simulate it by getting the highest note ID presently
// in the store, and incrementing it by 1.
// Used before creating a new local comment so IDs remain unique in mock mode.
export const getNextCommentId = async () => {
  try {
    const item = await read();
    if (!item.Items || item.Items.length === 0) return 0;
    let max = -Infinity;
    for (const c of item.Items) {
      if (!c) continue;
      const rawId = c.id ?? c.ID;
      const num = Number(rawId);
      if (!Number.isNaN(num) && isFinite(num)) {
        if (num > max) max = num;
      }
    }
    if (max === -Infinity) return 0;
    return max + 1;
  } catch (e) {
    return 0;
  }
};

export default {
  getCommentsForTicket,
  getNoteById,
  addComment,
  getNextCommentId,
  getUserAddedComments,
  restoreUserAddedComments,
  resetToDefaults,
  refreshDates,
};
